import {
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import { HistoryColumns } from '../config/columnsConfig';
import historyApi, {
  useCountCrawlHistoryQuery,
  useDeleteHistoryMutation
} from '@/app/Crawl/api/historyApi';
import { useOutletContext } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import CrawlBarCardHealth from '../components/history/BarchartHealth';
import CrawlBarCardPages from '../components/history/BarchartPages';
import CrawlBarCardStatus from '../components/history/BarchartIssues';
import HistoryPreload from '../components/history/HistoryPreload';
import NewCrawlForm from '../components/common/NewCrawlForm';
import NoCrawlData from '../components/common/NoCrawlData';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type { AgGridColumn } from '@/app/common/components/ag-grid/types';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import IconAlert from '@/components/common/IconAlert';
import { Link } from 'react-router-dom';
import { errorMessage, successMessage } from '@/utils/toast';
import { useDispatch } from 'react-redux';
import CancelModal from '@/app/common/components/CancelModal';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { NewCrawlContext, RemoveCrawlContext } from '@/context/Context';
import { getColor } from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
// صفحه تاریخچه خزش
type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type CrawlHistoryRow = {
  host_name: string;
  crawl_status: number;
  job_page: string;
  health_score?: number | string | null;
  count_page_internal_total?: number | null;
  count_page_external_total?: number | null;
  count_error?: number | null;
  count_warning?: number | null;
  count_info?: number | null;
  [key: string]: any;
};

type CrawlHistoryResponse = {
  data?: {
    rows?: CrawlHistoryRow[];
    total?: number;
    crawl_stoped?: boolean;
  };
};

const History = () => {
  const [, setBreadcrumbData] = useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'خزش جدید', href: '#' }]);
  }, []);
  // REMOVE HISTORY MODAL
  const {
    jobPage: remJobPage,
    start_url: remStartUrl,
    show: showRemModal,
    setShow: setShowRemModal
  } = useContext(RemoveCrawlContext);
  const [deleteHistory] = useDeleteHistoryMutation();

  //
  const alertAfterCrawlFinished = localStorage.getItem('alertAfterCrawlFinish');
  const { domain_slug, domain } = useDomain();
  useGetUserInfoQuery();
  useState(true);
  const [showAlertAfterCrawlFinished, setShowAfterCrawlFinished] =
    useState(false);
  const dispatch = useDispatch();
  const access = useToolAccess('crawl');
  useEffect(() => {
    if (alertAfterCrawlFinished) {
      setShowAfterCrawlFinished(true);
      localStorage.removeItem('alertAfterCrawlFinish');
    }
  }, [alertAfterCrawlFinished]);
  // دریافت تاریخچه خزش
  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useCountCrawlHistoryQuery(domain_slug, {
    skip: access.isLoading || !access.haveAccess
  });
  const { isSubmitted, jobPage } = useContext(NewCrawlContext);
  useEffect(() => {
    if (!isSubmitted || !jobPage) return;
    void refetch();
  }, [isSubmitted, jobPage, refetch]);
  const data = rawData as CrawlHistoryResponse | undefined;
  const historyRows = data?.data?.rows ?? [];
  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خزش" />;
  }
  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (isLoading) return <HistoryPreload />;
  if (!isLoading && !data?.data) return <NoCrawlData />;
  // throw new Error("باگ عجیب")
  return (
    <>
      {/* alert های بالای صفحه خزش */}
      <CancelModal
        setShow={setShowRemModal}
        show={showRemModal}
        submitTitle="آره حذف بشه"
        title="حذف اطلاعات خزش"
        submitFunc={() => {
          deleteHistory({ job_page: remJobPage, domain_slug })
            .unwrap()
            .then(() => {
              successMessage('خزش با موفقیت حذف شد');
              dispatch(
                historyApi.util.invalidateTags(['CrawlHistory'] as never)
              );
            })
            .catch(() => {
              errorMessage('در حذف خزش خطایی رخ داده');
            });
        }}
        desc={`آیا از حذف خزش ${remStartUrl} مطمئن هستی؟`}
      />
      <IconAlert
        show={showAlertAfterCrawlFinished}
        onClose={() => {
          setShowAfterCrawlFinished(false);
        }}
        variant="success"
        className="mt-2 mb-4"
        dismissible
      >
        <p className="mb-0 text-justify pe-3">
          بعد از اتمام خزش برای مشاهده خطاها و هشدارها باید وارد{' '}
          <Link to={`/crawl/check-list/last/${domain_slug}/${domain}`}>
            <span
              style={{
                color: getColor('navbar-vertical-default-link-active-color')
              }}
            >
              {' '}
              تب فهرست بازبینی{' '}
            </span>
          </Link>
          شوید.
        </p>
      </IconAlert>
      {/* فرم وارد کردن خزش جدید */}
      <NewCrawlForm
        lastCrawlStatus={historyRows[0]?.crawl_status}
        oldJobPage={historyRows[0]?.job_page}
        crawlStoped={data?.data?.crawl_stoped}
      />
      {/* نمودار های تاریخچه خزش */}
      <Row className="g-3 mb-3">
        <Col md={6} xl={4}>
          <CrawlBarCardHealth
            data={historyRows.slice().reverse()}
            isLoading={isLoading}
          />
        </Col>
        <Col md={6} xl={4}>
          <CrawlBarCardPages
            data={historyRows.slice().reverse()}
            isLoading={isLoading}
          />
        </Col>
        <Col md={6} xl={4}>
          <CrawlBarCardStatus data={historyRows.slice().reverse()} />
        </Col>
      </Row>
      <style>
        {`
        .for-style .ag-row-focus {
        z-index: 2;}`}
      </style>
      {/* جدول خزش */}
      <div className="for-style">
        <AgGridWrapper
          settings={{ pagination: false }}
          columns={
            HistoryColumns(
              domain_slug,
              historyRows[0]?.crawl_status
            ) as unknown as AgGridColumn[]
          }
          height="460px"
          total={data?.data?.total}
          data={historyRows.filter(
            (crawl: CrawlHistoryRow) => crawl.crawl_status !== 55
          )}
        />
      </div>
    </>
  );
};

export default History;
